(() => {
  'use strict';

  // Shared hover-preview mechanics for every homepage row that previews a
  // variant on hover: the cloth rows (Simonis, Championship), Cue Tips, Pool
  // Ball Sets and Single Balls. Consumed by assets/cloth-color-previews.js
  // (colour swatches) and assets/simonis-family-size-selector.js (size
  // buttons). The cue rows have their own copy of the same idea inside
  // assets/longoni-lightbox.js, which is where this was worked out first.
  //
  // The bug this exists to prevent: pointerout of one control fires BEFORE
  // pointerover of the next, so resetting on pointerout put the card back to
  // its selected variant for a frame on the way past. Measured on the draft
  // theme 2026-08-26, sliding across the six Simonis 860 size buttons:
  //
  //   ENTER 8-ft-pro -> price $327.00 (base!) -> $440.00
  //   ENTER 9-ft     -> price $327.00 (base!) -> $473.00
  //   ENTER 10-ft    -> price $327.00 (base!) -> $576.00
  //
  // - four returns to base in one sweep. Same on the way between a swatch and
  // a size button, which sit in different containers.
  //
  // The rule here: preview on entering a control, reset once the pointer
  // leaves the CONTAINER that control sits in - the swatch grid, the size
  // rail - and never between two controls of one container. The container
  // is what makes the sweep above a direct swap: pointerout of one swatch
  // lands on the next swatch or on the grid's own gaps, both inside it.
  //
  // It used to be the whole card, and that was the bug reported on
  // 2026-09-10: the card also holds BUY, the photo and its own white
  // ground, so pointing at a swatch and then moving down to BUY - the
  // commonest path there is - left the card showing the last swatch
  // hovered instead of the one clicked. Measured on Simonis, Championship,
  // Cue Tips, Chalk, Single Balls and Gloves, all the same way; leaving the
  // card outright was the only exit that restored. The cue tiles, which
  // restore per swatch through a decode gate in assets/longoni-lightbox.js,
  // were right on every exit and are the reference this now matches.
  //
  // Click commits; a restore always goes back to the last committed
  // choice, or to the card's default when nothing has been clicked.

  if (window.BCHoverPreview) return;

  // Where a pointer can travel without ending a preview. Each is one
  // control's container, gaps between its chips included.
  const ZONE_SELECTOR = '[data-cloth-color-options], [data-simonis-size-rail], [data-glove-swatches], [data-rr-swatches]';

  // The card is the group. Cloth family cards carry data-cloth-card and
  // data-simonis-family-card; the per-size panels inside them carry
  // data-cloth-size-card and are NOT the group - resetting on leaving a panel
  // is the cross-container flicker.
  //
  // The rail-rubber configurator cards are a group too. Leaving one out is
  // not a no-op that merely skips an optimisation: leavesGroup() resolves
  // the group with closest(), so a card that matches nothing here returns
  // null, "has the pointer left?" answers false, and the card never resets
  // to its selected item. Measured before it was added - sweeping the OKKO
  // thumbnails and then leaving the card left the price on the last one
  // hovered, $49.99 against the $54.99 that was actually selected.
  //
  // The glove cards are a group for the same reason the rubber ones are:
  // their swatch grid and their photo sit in one card, and a card missing
  // from this list makes leavesGroup() answer false forever, so the photo
  // would keep the last swatch hovered instead of the one selected.
  const GROUP_SELECTOR = '[data-cloth-card], [data-simonis-family-card], [data-rubber-config], [data-glove-card]';

  // One counter per group. Every image swap takes the next number and only
  // paints if it is still the newest when its decode finishes, so a fast
  // sweep cannot have an early swap land after a later one.
  const tokens = new WeakMap();

  function hoverCapable() {
    try {
      return window.matchMedia('(hover: hover)').matches;
    } catch (error) {
      return true;
    }
  }

  // Distinguishes real keyboard focus from the focus a pointer press also
  // produces - without it a tap runs the hover path as well as the click one.
  function focusVisible(element) {
    try {
      return Boolean(element && element.matches && element.matches(':focus-visible'));
    } catch (error) {
      return true;
    }
  }

  function group(node) {
    return node instanceof Element ? node.closest(GROUP_SELECTOR) : null;
  }

  /*
    THE ONE REVERT TRIGGER (2026-09-24, the owner's rule after the third
    report of the flicker). A preview reverts ONLY when the pointer leaves
    the whole swatch grid - pointerleave on the grid element itself -
    never on leaving one swatch, never in the gap between two, and never
    from anything a card, a lane or the pointer's coordinates say. Every
    row registers its grid selector and its restore here, and ONE capture
    listener on the document serves them all: pointerleave does not
    bubble, but the capture phase still walks down to the element being
    left, so a single listener sees every grid on the page, including
    the rows that arrive later through their lazy slots.

    WHY THE GAPS FLICKERED, measured 2026-09-24 on the Vaula Pro card
    (one sweep: six previews, six reverts, one per gap or row crossed).
    assets/bc-card-link.js lays a whole-card <a> over every card at
    z-index 1 and lifts the controls above it - but not the grid they sit
    in. A gap between two swatches therefore belonged to the OVERLAY, not
    to the grid, and pointerout from a swatch reported a relatedTarget
    outside the zone: "left the grid", revert, then the next swatch
    previewed again. The grid is lifted with its controls now
    (bc-card-link.js, CONTROLS), so its gaps are its own box and
    pointerleave fires once, at its edge. The Simonis colour grid never
    flickered for exactly this reason: its stylesheet already positions
    it at z-index 1, above the overlay.

    The coordinate test that stood here (overBox, 2026-09-23) is gone
    with the rule: a preview fires from pointerover on the swatch element
    and nothing else.
  */
  const leaveHandlers = [];

  function onZoneLeave(zoneSelector, callback) {
    if (!zoneSelector || typeof callback !== 'function') return;
    leaveHandlers.push({ selector: zoneSelector, callback: callback });
  }

  document.addEventListener('pointerleave', function (event) {
    if (!hoverCapable()) return;
    const zone = event.target;
    if (!(zone instanceof Element) || !zone.matches) return;
    for (let i = 0; i < leaveHandlers.length; i++) {
      if (zone.matches(leaveHandlers[i].selector)) leaveHandlers[i].callback(zone, event);
    }
  }, true);

  // True only when focus has genuinely left the card (the keyboard path's
  // revert). relatedTarget is null when it left the document, which counts
  // as leaving.
  function leavesGroup(from, relatedTarget) {
    const owner = group(from);
    if (!owner) return false;
    if (!(relatedTarget instanceof Node)) return true;
    return !owner.contains(relatedTarget);
  }

  function nextToken(owner) {
    if (!owner) return 0;
    const value = (tokens.get(owner) || 0) + 1;
    tokens.set(owner, value);
    return value;
  }

  function currentToken(owner) {
    return owner ? tokens.get(owner) || 0 : 0;
  }

  // Never writes an empty string: a blank price is worse than a stale one,
  // and an empty write was how the cue rows used to flash a gap.
  function setText(element, value) {
    if (!element) return;
    const next = value == null ? '' : String(value);
    if (!next) return;
    if (element.textContent !== next) element.textContent = next;
  }

  function writeImage(image, data) {
    // srcset before src: assigning src while the previous srcset is still on
    // the element runs candidate selection against the OLD list, paints that,
    // then re-selects when the new srcset lands - two paints, the second of
    // which is the flash of the previous variant.
    if (data.srcset) {
      if (image.getAttribute('srcset') !== data.srcset) image.srcset = data.srcset;
    } else if (image.hasAttribute('srcset')) {
      image.removeAttribute('srcset');
    }

    if (image.getAttribute('src') !== data.src) image.src = data.src;
    if (data.alt != null) image.alt = data.alt;
  }

  // Decode first, then swap. Until the new file is ready the current one
  // stays on screen - no placeholder, no blank frame. `immediate` skips the
  // decode for the restore-to-base path, which has to land in one synchronous
  // write so that leaving the card cannot leave a half-restored card behind.
  function swapImage(image, data, options) {
    if (!image || !data || !data.src) return;

    const settings = options || {};
    const owner = settings.group || group(image);
    const my = nextToken(owner);

    if (settings.immediate) {
      writeImage(image, data);
      return;
    }

    if (image.getAttribute('src') === data.src) {
      writeImage(image, data);
      return;
    }

    let done = false;
    const finish = () => {
      if (done) return;
      // A newer hover has already been asked for: this one is stale, drop it.
      if (currentToken(owner) !== my) return;
      done = true;
      writeImage(image, data);
    };

    const preload = new Image();
    if (data.sizes) preload.sizes = data.sizes;
    if (data.srcset) preload.srcset = data.srcset;
    preload.src = data.src;

    if (preload.decode) {
      preload.decode().then(finish).catch(finish);
    } else if (preload.complete) {
      finish();
    } else {
      preload.onload = finish;
      preload.onerror = finish;
    }

    // Relief valve: a decode that never settles must not strand the card on
    // the previous colour for good.
    window.setTimeout(finish, 1200);
  }

  window.BCHoverPreview = {
    GROUP_SELECTOR,
    ZONE_SELECTOR,
    hoverCapable,
    focusVisible,
    group,
    leavesGroup,
    onZoneLeave,
    nextToken,
    currentToken,
    setText,
    swapImage
  };
})();
